<template>
	<div
		class="page"
		:class="[
			`launch-grid-${computedEditorLayout}${showVariableViewer ? '-variableViewer' : ''}`,
			{ 'page--wide': computedEditorLayout === 'horizontal' },
		]"
	>
		<div class="grid-editor">
			<Editor
				editorHeight="100%"
				id="editor"
				class="userflow-code-editor"
				ref="editor"
				alt="Editor"
				:taskID="taskID"
				:taskStatus="taskStatus"
				:activeTask="activeTask"
				:taskAccessState="mergedTaskAccessState"
				:buildID="buildID"
				:buildStatus="buildStatus"
				:buildAccessState="mergedBuildAccessState"
				:activeBuild="activeBuild"
				:editorLayout="computedEditorLayout"
				:variableViewerVisibility="showVariableViewer"
				@request-build="submitTaskRequest"
				@code-changed="calculateCodeHash"
				@create-task-request="submitBuildRequest"
				@cancel-active-task-request="cancelActiveTask"
				@cancel-active-build-request="cancelActiveBuild"
				@retry-task-output="retryTaskOutput"
				@retry-build-output="retryBuildOutput"
			/>
			<!-- @layout-change-request="changeLayout" -->
		</div>

		<div
			v-show="showVariableViewer"
			class="grid-variableViewer"
		>
			<VariableViewer
				id="variableViewer"
				ref="variableViewer"
				height="100%"
				:variablesList="completeVariablesList"
				:variableDiscoveryInProgress="variableDiscoveryInProgress"
				:referenceCoreChosen="referenceCoreChosen()"
				:currentCodeHash="codeHash"
				@discover-variables="discoverVariables"
				@close-panel="editorStore.setVariableViewerVisibility(false, true)"
				@switch-to-ref-core="switchToReferenceCore"
				@add-custom-trace-expression="addCustomTraceExpression"
				@remove-custom-trace-expression="removeCustomTraceExpression"
			/>
		</div>

		<div class="grid-resultsPanel userflow-results-panel-editor">
			<Panel
				:taskID="taskID"
				:buildID="buildID"
			/>
		</div>
	</div>
</template>

<script lang="ts">
import { defineComponent, provide, ref } from "vue";
import { useEventBus } from "@vueuse/core";
import { useCancelWithFeedback, CancelFeedbackKey } from "@/composables/useCancelWithFeedback";

// Types
import { TraceVariableWithTraceType } from "@/types/api/nodes";
import { BuildBusEvent, RemoteAccessState, ResultsPanelTab, TaskBusEvent } from "@/types/general";
import { UserLimitsE } from "@/js/tierconfig";
import { TaskStatus, Task, isTaskTerminated, TaskStatusE } from "@/types/api/tasks";

// Libraries
import { sha256 } from "js-sha256";

// Utilities
import * as Sentry from "@sentry/vue";
import { sdk } from "@/js/sdk";
import { extractSdkErrorMessage } from "@/js/sdkError";
import { SdkError, ERROR_CODES } from "@signaloid/cloud-compute-engine-client";
import * as util from "@/js/utilities";
import { isAxiosTimeoutError, monitoringCaptureError } from "@/plugins/monitoring";

// Components
import Editor from "@/components/Editor.vue";
import Panel from "@/components/Panel.vue";
import VariableViewer from "@/components/DevelopmentPlatform/VariableViewer.vue";

// Stores
import { useTasksStore } from "@/stores/tasks";
import { useEditorStore } from "@/stores/editor";
import { useRootStore } from "@/stores/root";

// Global variables
import { defaultTabs } from "@/assets/defaultTabs";
import { useCoresStore } from "@/stores/cores";
import { useUserStore } from "@/stores/user";
import { TierLimitEventTypeE } from "@/eventBus/tierLimitEventBus";
import { tourEventKey, TourBusEvent } from "@/eventBus/tourEventBus";
import { useOutputsStore } from "@/stores/outputs";
import {
	Build,
	BuildStatus,
	BuildStatusE,
	canFetchVariables,
	isBuildFail,
	isBuildTerminated,
} from "@/types/api/builds";
import { shouldSkipBuildOutputForError } from "@/types/buildErrors";
import { SourceCodeLanguages } from "@/types/api/sourceCode";
import { useBuildsStore } from "@/stores/builds";

// Local Types
type ViewportAreaSize = { height: string; width: string };
type ViewportArea = { [key: string]: ViewportAreaSize };
type ViewportArrangement = { [key: string]: ViewportArea };

type ComponentData = {
	taskID: undefined | string;
	taskStatus: undefined | TaskStatus;
	activeTask: undefined | Task;
	buildID: undefined | string;
	buildStatus: undefined | BuildStatus;
	activeBuild: undefined | Partial<Build>;
	results: undefined | ResultsPanelTab[];
	resultsLoading: boolean;
	viewportRatio: ViewportArrangement;
	discoveredVariables: TraceVariableWithTraceType[];
	customVariableExpressions: TraceVariableWithTraceType[];
	variableDiscoveryInProgress: boolean;
	codeHash: undefined | string;
	taskAccessState: RemoteAccessState;
	buildAccessState: RemoteAccessState;
	taskEventBusSub: undefined | Function;
	buildEventBusSub: undefined | Function;
	tourEventBusSub: undefined | Function;
	lastBuildForVariableDiscovery: boolean;
	hasTrackedFirstBuild: boolean;
	hasTrackedFirstTask: boolean;
	submittingTask: boolean;
};

export default defineComponent({
	name: "DevelopmentPlatform",
	components: { Editor, Panel, VariableViewer },
	data: (): ComponentData => ({
		taskID: undefined,
		taskStatus: undefined,
		activeTask: undefined,
		buildID: undefined,
		buildStatus: undefined,
		activeBuild: undefined,
		resultsLoading: false,
		results: undefined,
		viewportRatio: {
			horizontal: {
				editorPanel: { height: "75vh", width: "8" },
				resultsPanel: { height: "75vh", width: "4" },
				variableViewer: { height: "75.2vh", width: "3" },
			},
			vertical: {
				editorPanel: { height: "50vh", width: "12" },
				resultsPanel: { height: "20vh", width: "12" },
				variableViewer: { height: "50vh", width: "4" },
			},
			stacked: {
				editorPanel: { height: "45vh", width: "12" },
				resultsPanel: { height: "20vh", width: "12" },
				variableViewer: { height: "20vh", width: "12" },
			},
		},
		discoveredVariables: [],
		customVariableExpressions: [],
		variableDiscoveryInProgress: false,
		codeHash: undefined,
		taskAccessState: {
			loading: false,
			status: undefined,
			error: false,
			message: "",
		},
		buildAccessState: {
			loading: false,
			status: undefined,
			error: false,
			message: "",
		},
		taskEventBusSub: undefined,
		buildEventBusSub: undefined,
		tourEventBusSub: undefined,
		lastBuildForVariableDiscovery: false,
		hasTrackedFirstBuild: false,
		hasTrackedFirstTask: false,
		submittingTask: false,
	}),
	setup() {
		const tasksStore = useTasksStore();
		const buildsStore = useBuildsStore();
		const editorStore = useEditorStore();
		const rootStore = useRootStore();
		const coresStore = useCoresStore();
		const userStore = useUserStore();
		const outputsStore = useOutputsStore();
		const editor = ref<InstanceType<typeof Editor>>();
		const variableViewer = ref<InstanceType<typeof VariableViewer>>();

		const cancel = useCancelWithFeedback();
		provide(CancelFeedbackKey, cancel);

		const tourBus = useEventBus(tourEventKey);

		return {
			tasksStore,
			buildsStore,
			editorStore,
			rootStore,
			coresStore,
			userStore,
			editor,
			variableViewer,
			util,
			outputsStore,
			cancel,
			tourBus,
		};
	},

	computed: {
		// activeTask(): undefined | Task {
		// 	// FIXME: get task data from the store if taskID is defined.
		// 	if (this.taskID) {
		// 		return this.tasksStore.getTaskByID(this.taskID) ?? undefined;
		// 	} else {
		// 		return undefined;
		// 	}
		// },
		resultsPanelText(): readonly ResultsPanelTab[] {
			return this.results ?? defaultTabs;
		},

		computedEditorLayout: function (): string {
			if (this.$vuetify.display.xs) {
				return "stacked";
			} else if (this.$vuetify.display.sm) {
				if (this.showVariableViewer) {
					return "stacked";
				} else {
					return "vertical";
				}
			} else if (this.$vuetify.display.md || this.$vuetify.display.lg) {
				if (this.showVariableViewer) {
					return "vertical";
				} else {
					return this.editorStore.editorLayout;
				}
			} else {
				return this.editorStore.editorLayout;
			}
		},
		completeVariablesList(): TraceVariableWithTraceType[] {
			return [...this.discoveredVariables, ...this.customVariableExpressions];
		},
		showVariableViewer(): boolean {
			return this.editorStore.variableViewerVisibility ?? false;
		},
		mergedTaskAccessState(): RemoteAccessState {
			return util.mergeAccessState(this.outputsStore.getTaskOutputAccessState(this.taskID), this.taskAccessState);
		},
		mergedBuildAccessState(): RemoteAccessState {
			return util.mergeAccessState(
				this.outputsStore.getBuildOutputAccessState(this.buildID),
				this.buildAccessState,
			);
		},
	},
	methods: {
		handleTourResetEditor(event: TourBusEvent) {
			if (event.type === "reset-editor") {
				(this.$refs.editor as any)?.resetCodeToDefault();
			}
		},
		/*
		 *	Translate a build-submission error into UI status + tier-limit
		 *	bus emission. Both `submitBuildRequest` and `discoverVariables`
		 *	share this path since they hit the same SDK endpoint with the
		 *	same failure modes.
		 */
		handleBuildSubmissionError(error: unknown) {
			const detailsMessage = extractSdkErrorMessage(error);

			if (error instanceof SdkError) {
				switch (error.code) {
					case ERROR_CODES.API_FORBIDDEN: {
						this.buildStatus = BuildStatusE.TierLimited;
						const isComputeLimit = detailsMessage.toLowerCase().includes("dynamic instruction");
						this.buildAccessState.message = isComputeLimit
							? "You have reached the monthly computation limit for your account." +
								" Please upgrade your account in https://signaloid.io/billing to run more builds." +
								" If you have further questions, please contact support at developer-support@signaloid.com."
							: "You have reached the limit of available concurrently-running builds for your account." +
								" Please upgrade your account in https://signaloid.io/billing to enable builds running concurrently." +
								" If you have further questions, please contact support at developer-support@signaloid.com.";
						this.rootStore.tierLimitEventBus.emit({
							type: TierLimitEventTypeE.LimitExceeded,
							affectedLimits: [
								isComputeLimit ? UserLimitsE.DynamicInstructionCount : UserLimitsE.ConcurrentBuildCount,
							],
						});
						break;
					}
					case ERROR_CODES.API_UNAUTHORIZED:
						this.buildStatus = BuildStatusE.Error;
						this.buildAccessState.message =
							"Our system encountered an authorization error while starting the build." +
							" Signing out and back in will ensure you are properly authorized." +
							" If this error persists, please contact support at developer-support@signaloid.com.";
						break;
					case ERROR_CODES.API_SERVER_ERROR:
					case ERROR_CODES.API_FATAL:
						this.buildStatus = BuildStatusE.Error;
						this.buildAccessState.message =
							"Our system encountered an internal error while starting the build." +
							" Our team will receive a high-priority notification for this issue." +
							" If this error persists, please contact support at developer-support@signaloid.com.";
						break;
					default:
						this.buildStatus = BuildStatusE.Error;
						this.buildAccessState.message =
							"Our system encountered an error while starting the new build." +
							" Please try starting the build again." +
							" If this error persists, please contact support at developer-support@signaloid.com.";
				}
			} else {
				this.buildStatus = BuildStatusE.Error;
				this.buildAccessState.message =
					"The build request could not be completed due to an unexpected error." +
					" If this error persists, please contact support at developer-support@signaloid.com.";
			}

			this.buildAccessState.error = true;
			Sentry.captureException(new Error("Build rejected"), {
				level: isAxiosTimeoutError(error) ? "info" : "error",
				extra: { error, userMessage: this.buildAccessState.message },
				tags: { service: "create-build" },
			});
		},
		handleTaskSubmissionError(error: unknown) {
			const detailsMessage = extractSdkErrorMessage(error);

			if (error instanceof SdkError) {
				switch (error.code) {
					case ERROR_CODES.API_FORBIDDEN: {
						this.taskStatus = TaskStatusE.TierLimited;
						const isComputeLimit = detailsMessage.toLowerCase().includes("dynamic instruction");
						this.taskAccessState.message = isComputeLimit
							? "You have reached the monthly computation limit for your account." +
								" Please upgrade your account in https://signaloid.io/billing to run more tasks." +
								" If you have further questions, please contact support at developer-support@signaloid.com."
							: "You have reached the limit of available concurrently-running tasks for your account." +
								" Please upgrade your account in https://signaloid.io/billing to enable tasks running concurrently." +
								" If you have further questions, please contact support at developer-support@signaloid.com.";
						this.rootStore.tierLimitEventBus.emit({
							type: TierLimitEventTypeE.LimitExceeded,
							affectedLimits: [
								isComputeLimit ? UserLimitsE.DynamicInstructionCount : UserLimitsE.ConcurrentTaskCount,
							],
						});
						break;
					}
					case ERROR_CODES.API_UNAUTHORIZED:
						this.taskStatus = TaskStatusE.Error;
						this.taskAccessState.message =
							"Our system encountered an authorization error while starting the task." +
							" Signing out and back in will ensure you are properly authorized." +
							" If this error persists, please contact support at developer-support@signaloid.com.";
						break;
					case ERROR_CODES.API_SERVER_ERROR:
					case ERROR_CODES.API_FATAL:
						this.taskStatus = TaskStatusE.Error;
						this.taskAccessState.message =
							"Our system encountered an internal error while starting the task." +
							" Our team will receive a high priority notification for this issue." +
							" If this error persists, please contact support at developer-support@signaloid.com.";
						break;
					default:
						this.taskStatus = TaskStatusE.Error;
						this.taskAccessState.message =
							"Our system encountered an error while starting the new task." +
							" Please try starting the task again." +
							" If this error persists, please contact support at developer-support@signaloid.com.";
				}
			} else {
				this.taskStatus = TaskStatusE.Error;
				this.taskAccessState.message =
					"The task request could not be completed due to an unexpected error." +
					" If this error persists, please contact support at developer-support@signaloid.com.";
			}

			this.taskAccessState.error = true;
			Sentry.captureException(new Error("Task rejected"), {
				level: isAxiosTimeoutError(error) ? "info" : "error",
				extra: { error, userMessage: this.taskAccessState.message },
				tags: { service: "create-task" },
			});
		},
		async retryTaskOutput() {
			if (!this.taskID) return;
			await this.outputsStore.retryOutputs({ taskID: this.taskID });
		},
		async retryBuildOutput() {
			if (!this.buildID) return;
			await this.outputsStore.retryOutputs({ buildID: this.buildID });
		},
		async submitBuildRequest() {
			this.buildAccessState = {
				loading: false,
				status: undefined,
				error: false,
				message: "",
			};
			this.taskAccessState = {
				loading: false,
				status: undefined,
				error: false,
				message: "",
			};
			this.taskStatus = TaskStatusE.Pending;
			this.buildStatus = BuildStatusE.Pending;
			this.activeTask = undefined;
			this.activeBuild = undefined;
			this.buildID = undefined;
			this.taskID = undefined;
			try {
				const sourceCode = this.editor?.getCode();
				const language = this.editor?.getSelectedLanguage();
				const activeCore = this.editor?.getActiveCore();
				const variableTraceList = this.variableViewer?.getTraceVariables();
				const compilationFlags = this.editor?.getCompilationFlags();

				if (activeCore?.Deprecated) {
					this.buildStatus = BuildStatusE.Error;
					this.taskStatus = TaskStatusE.NotStarted;
					this.buildAccessState.error = true;
					this.buildAccessState.message = this.coresStore.deprecatedCoreBuildErrorMessage(activeCore.Name);
					return;
				}

				const sourceCodeAsBase64 = Buffer.from(sourceCode ?? "").toString("base64");

				const response = await sdk.builds.createFromSourceCode({
					Code: sourceCodeAsBase64 ?? "",
					Language: (language?.name as SourceCodeLanguages) || "C",
					CoreID: activeCore?.CoreID,
					TraceVariables: variableTraceList,
					...(compilationFlags ? { ConfigMk: compilationFlags } : {}),
				});
				this.lastBuildForVariableDiscovery = false;

				const buildResponse = (await sdk.builds.getOne(response.BuildID)) as Build;

				this.buildID = response.BuildID;
				this.activeBuild = buildResponse;
				this.buildStatus = BuildStatusE.Accepted;

				console.log(`Build created ID: ${this.activeBuild?.BuildID}`);
				// @ts-ignore
				this.$posthog?.capture("build_created", {
					buildType: "SourceCode",
					buildID: this.activeBuild?.BuildID,
				});

				this.buildsStore.addToActiveBuildList(buildResponse);
			} catch (error) {
				this.variableDiscoveryInProgress = false;
				this.handleBuildSubmissionError(error);
				if (!this.taskID) {
					this.taskStatus = TaskStatusE.NotStarted;
				}
			}
		},

		async submitTaskRequest() {
			if (!this.buildID || this.submittingTask) {
				return;
			}

			this.submittingTask = true;
			this.taskID = undefined;
			try {
				const runArguments = this.editor?.getRunArguments();
				const dataSources = await this.editor?.getDataSources();

				const response = await sdk.tasks.createTask(this.buildID, {
					Arguments: runArguments,
					DataSources: dataSources ?? [],
				});

				const taskResponse = (await sdk.tasks.getOne(response.TaskID)) as Task;

				this.activeTask = taskResponse;
				this.taskID = response.TaskID;
				this.taskStatus = taskResponse.Status;

				console.log(`Task created ID: ${this.activeTask.TaskID}`);

				// @ts-ignore
				this.$posthog?.capture("task_created", {
					buildId: this.activeTask?.BuildID,
					taskId: this.activeTask?.TaskID,
				});

				this.tasksStore.addToActiveTaskList(this.activeTask);
				/*
				 *	At this point the task has been accepted and will be queued to be run.
				 *	Any subsequent tasks will return with 403 if the concurrent task limit has been reached.
				 *	The task status updates will be handled by `taskEventBusCallback`
				 */
			} catch (error) {
				this.variableDiscoveryInProgress = false;
				this.handleTaskSubmissionError(error);
			} finally {
				this.submittingTask = false;
			}
		},
		async cancelActiveTask() {
			const taskID = this.taskID;
			await this.cancel.cancelTask(taskID, {
				onSent: () => {
					// @ts-ignore
					this.$posthog?.capture("task_cancelled", {
						task_id: taskID,
						build_id: this.buildID,
						status: "Cancelled",
					});
				},
			});
		},
		async cancelActiveBuild() {
			const buildID = this.buildID;
			await this.cancel.cancelBuild(buildID);
			// @ts-ignore
			this.$posthog?.capture("build_cancelled", {
				build_id: buildID,
				build_type: "SourceCode",
				status: "Cancelled",
			});
		},
		async discoverVariables() {
			// FIXME: use post project end point to build and run on the default core and get the variable list
			// TODO: Update this to use Modules endpoint or tasks with build only query param.

			try {
				this.variableDiscoveryInProgress = true;
				const sourceCode = this.editor?.getCode();
				const language = this.editor?.getSelectedLanguage();

				const sourceCodeAsBase64 = Buffer.from(sourceCode ?? "").toString("base64");

				const discoveryCore = this.coresStore.getSmallestDefaultCoreForDiscoveringVariables;

				if (!discoveryCore?.CoreID) {
					throw new Error("No suitable core available for variable discovery");
				}

				const response = await sdk.builds.createFromSourceCode(
					{
						Code: sourceCodeAsBase64 ?? "",
						Language: (language?.name as SourceCodeLanguages) || "C",
						CoreID: discoveryCore.CoreID,
					},
					true,
				);
				this.lastBuildForVariableDiscovery = true;

				const buildResponse = (await sdk.builds.getOne(response.BuildID)) as Build;

				this.activeBuild = buildResponse;
				this.buildID = response.BuildID;
				this.buildStatus = buildResponse.Status as BuildStatus;

				console.log(`Build created ID: ${this.activeBuild?.BuildID}`);
				// @ts-ignore
				this.$posthog?.capture("build_created", {
					buildType: "SourceCode",
					buildID: this.activeBuild?.BuildID,
				});

				if (this.buildEventBusSub == undefined) {
					this.subscribeToBuildEventBus();
				}

				this.buildsStore.addToActiveBuildList(buildResponse);
			} catch (error) {
				this.variableDiscoveryInProgress = false;
				monitoringCaptureError(error, "Discover variables");
			}
		},
		switchToReferenceCore() {
			this.editor?.switchToReferenceCore();
		},
		referenceCoreChosen() {
			try {
				const activeCore = this.editor?.getActiveCore();
				return activeCore?.Microarchitecture == "Reference";
			} catch {
				return false;
			}
		},
		// // TODO:
		// dragStart(event, a) {
		// 	event.dataTransfer.dropEffect = "move";
		// 	event.dataTransfer.effectAllowed = "move";
		// 	event.preventDefault();
		// 	const target = event.target;
		// 	console.log("event", event);
		// },
		// // TODO:
		// onDropHandle(event, item) {
		// 	const target = event.target;
		// 	console.log("event", event);
		// },
		// getLayoutDimensions(panelName) {
		// 	if (this.showVariableViewer) {
		// 	}
		// 	this.viewportRatio[this.computedEditorLayout][panelName];
		// },
		calculateCodeHash() {
			try {
				const code = this.editor?.getCode();
				this.codeHash = sha256(code ?? "");
			} catch (error) {
				monitoringCaptureError(error, "Calculate code editor source hash");
			}
		},
		addCustomTraceExpression(variableTraceToAdd: TraceVariableWithTraceType) {
			this.customVariableExpressions.push(variableTraceToAdd);
		},
		removeCustomTraceExpression(variableTraceToRemove: TraceVariableWithTraceType) {
			const traceVariableIndex = this.customVariableExpressions.findIndex((v) => {
				return (
					v.Expression == variableTraceToRemove.Expression &&
					v.LineNumber == variableTraceToRemove.LineNumber &&
					v.File == variableTraceToRemove.File
				);
			});
			this.customVariableExpressions.splice(traceVariableIndex, 1);
		},

		async buildEventBusCallback(event: BuildBusEvent) {
			// @ts-ignore FIXME:
			if (this.buildID && event.buildData.BuildID === this.buildID) {
				this.activeBuild = event.buildData;
				this.buildStatus = event.buildData.Status;

				const { wasCancelledByUser: buildWasCancelledByUser } = this.cancel.handleBuildStatusUpdate(
					this.buildID,
					this.buildStatus,
				);
				const normalBuildExecution =
					this.buildStatus == "Completed" && !this.lastBuildForVariableDiscovery && !buildWasCancelledByUser;
				const variableDiscoveryFailed = isBuildFail(this.buildStatus) && this.lastBuildForVariableDiscovery;
				const buildFail = isBuildFail(this.buildStatus) && !this.lastBuildForVariableDiscovery;

				if (this.lastBuildForVariableDiscovery && this.buildStatus && canFetchVariables(this.buildStatus)) {
					try {
						// fetch task variables
						this.buildsStore.fetchAndParseVariables(this.buildID).then((result) => {
							this.discoveredVariables = result;
							this.variableDiscoveryInProgress = false;
						});
					} catch (error) {
						monitoringCaptureError(error, "Handle active source task status change");
					}
				}

				// PostHog: Track build completion and failure
				if (this.buildStatus == "Completed" && !this.lastBuildForVariableDiscovery) {
					// @ts-ignore
					this.$posthog?.capture("build_completed", {
						build_id: this.buildID,
						build_type: "SourceCode",
						status: "Completed",
					});
					if (!this.hasTrackedFirstBuild) {
						this.hasTrackedFirstBuild = true;
						// @ts-ignore
						this.$posthog?.capture("first_build_completed", {
							build_type: "SourceCode",
							$set_once: { first_build_at: new Date().toISOString() },
						});
					}
				}
				if (
					isBuildTerminated(this.buildStatus) &&
					this.buildStatus !== BuildStatusE.Completed &&
					this.buildStatus !== BuildStatusE.Cancelled &&
					!this.lastBuildForVariableDiscovery
				) {
					// @ts-ignore
					this.$posthog?.capture("build_failed", {
						build_id: this.buildID,
						build_type: "SourceCode",
						status: this.buildStatus,
					});
				}

				// Handle variable discovery failures - stop spinner if build terminated without success
				if (
					this.lastBuildForVariableDiscovery &&
					isBuildTerminated(this.buildStatus) &&
					!canFetchVariables(this.buildStatus)
				) {
					this.variableDiscoveryInProgress = false;
				}

				if (normalBuildExecution && !this.taskID && !this.submittingTask) {
					// Status stays Pending until submitTaskRequest sets the real one.
					this.submitTaskRequest();
				} else if (
					isBuildTerminated(this.buildStatus) &&
					!this.taskID &&
					!this.submittingTask &&
					!this.lastBuildForVariableDiscovery
				) {
					// Build terminated without success — task will never start.
					this.taskStatus = TaskStatusE.NotStarted;
				}

				const buildWasCancelled = this.buildStatus === BuildStatusE.Cancelled;
				// Skip the fetch only for codes in BUILD_ERRORS_SKIP_OUTPUT (currently just clone); all others fetch.
				const skipBuildOutput = shouldSkipBuildOutputForError(
					util.parseBuildError(event.buildData.StatusTransitions, this.buildStatus).code,
				);
				if ((variableDiscoveryFailed || buildFail) && !buildWasCancelled && !skipBuildOutput) {
					try {
						await this.outputsStore.loadBuildOutputs(this.buildID);
					} catch {
						// Error state is populated in outputsStore and surfaced via BuildBar.
					}
				}
			}
		},
		async taskEventBusCallback(event: TaskBusEvent) {
			// console.log("taskEventBusSub event (in launch tab): ", event);
			// @ts-ignore FIXME:
			if (this.taskID && event.taskData.TaskID === this.taskID) {
				// console.log("update for this task :>> status = ", event.taskData.Status);
				this.activeTask = event.taskData;
				this.taskStatus = event.taskData.Status;

				this.cancel.handleTaskStatusUpdate(event.taskData.TaskID, event.taskData.Status);

				// PostHog: Track task completion
				if (this.taskStatus === "Completed") {
					// @ts-ignore
					this.$posthog?.capture("task_completed", {
						task_id: this.taskID,
						build_id: this.buildID,
						status: "Completed",
					});
					if (!this.hasTrackedFirstTask) {
						this.hasTrackedFirstTask = true;
						// @ts-ignore
						this.$posthog?.capture("first_task_completed", {
							task_id: this.taskID,
							$set_once: { first_task_at: new Date().toISOString() },
						});
					}
				}

				// PostHog: Track task failure (terminated but not completed or cancelled)
				const taskFailed =
					isTaskTerminated(this.taskStatus) &&
					this.taskStatus !== TaskStatusE.Completed &&
					this.taskStatus !== TaskStatusE.Cancelled;
				if (taskFailed) {
					// @ts-ignore
					this.$posthog?.capture("task_failed", {
						task_id: this.taskID,
						build_id: this.buildID,
						status: this.taskStatus,
					});
				}

				const taskWasCancelled = this.taskStatus === TaskStatusE.Cancelled;
				if (isTaskTerminated(this.taskStatus) && !taskWasCancelled) {
					try {
						await this.outputsStore.loadTaskOutputs(this.taskID);
						this.buildID && (await this.outputsStore.loadBuildOutputs(this.buildID));
					} catch {
						// Error state is populated in outputsStore and surfaced via BuildBar.
					}
				}
			}
		},
		subscribeToBuildEventBus() {
			this.buildEventBusSub = this.buildsStore.buildEventBus.on(this.buildEventBusCallback);
		},
		subscribeToTaskEventBus() {
			this.taskEventBusSub = this.tasksStore.taskEventBus.on(this.taskEventBusCallback);
		},
	},
	mounted() {
		// Create a subscription to the task/build event bus
		this.subscribeToTaskEventBus();
		this.subscribeToBuildEventBus();

		this.buildID = this.editorStore.getLastBuild;

		// if (this.$route.query.pid != undefined && typeof this.$route.query.pid == "string") {
		// 	this.projectID = this.$route.query.pid;
		// }

		this.calculateCodeHash();

		this.tourEventBusSub = this.tourBus.on(this.handleTourResetEditor);
	},
	beforeUnmount() {
		if (this.buildEventBusSub !== undefined) {
			this.buildEventBusSub();
		}
		// Unsubscribe from the task even bus on dismount
		if (this.taskEventBusSub !== undefined) {
			this.taskEventBusSub();
		}

		if (this.tourEventBusSub !== undefined) {
			this.tourEventBusSub();
		}
	},
});
</script>

<style>
input#arguments {
	font-variant-ligatures: common-ligatures;
	font-family: var(--sgnl-font-family-mono);
	font-size: var(--sgnl-font-size-paragraph-s);
}
</style>

<style lang="scss" scoped>
.launch-grid {
	display: grid;
	gap: var(--sgnl-space-8);
	height: 100%;
	padding-bottom: var(--sgnl-space-32);

	grid-template-columns: minmax(300px, 1fr);
	grid-template-rows: minmax(300px, 60vh) minmax(200px, 250px) minmax(300px, 1fr);
	grid-template-areas:
		"editor"
		"variableViewer"
		"resultsPanel";
}

.launch-grid-stacked {
	@extend .launch-grid;
	grid-template-areas:
		"editor"
		"editor"
		"resultsPanel";
}
.launch-grid-stacked-variableViewer {
	@extend .launch-grid;
	grid-template-areas:
		"editor"
		"variableViewer"
		"resultsPanel";
}

.launch-grid-vertical {
	@extend .launch-grid;
	grid-template-columns: minmax(300px, 1fr);
	grid-template-rows: minmax(300px, 60vh) minmax(300px, 35vh);
	grid-template-areas:
		"editor"
		"resultsPanel";
}
.launch-grid-vertical-variableViewer {
	@extend .launch-grid;
	grid-template-columns: minmax(300px, 3fr) minmax(450px, 1fr);
	grid-template-rows: minmax(300px, 60vh) minmax(300px, 35vh);
	overflow: hidden;
	grid-template-areas:
		"editor variableViewer"
		"resultsPanel resultsPanel";
}

.launch-grid-horizontal {
	@extend .launch-grid;
	grid-template-columns: minmax(300px, 3fr) minmax(300px, 2fr);
	grid-template-rows: minmax(100%, 80vh);
	grid-template-areas: "editor resultsPanel";
}

.launch-grid-horizontal-variableViewer {
	@extend .launch-grid;
	grid-template-columns: minmax(300px, 3fr) minmax(300px, auto) minmax(300px, 2fr);
	grid-template-rows: minmax(100%, 80vh);
	grid-template-areas: "editor variableViewer resultsPanel";
}

.grid-editor {
	grid-area: editor;
	align-self: stretch;
}
.grid-variableViewer {
	grid-area: variableViewer;
}
.grid-resultsPanel {
	grid-area: resultsPanel;
}
</style>
